// API Response Types

export interface ApiResponse<T> {
  data: T;
  totalCount?: number;
  message?: string;
}

export interface Source {
  id: number;
  name: string;
  displayName: string;
  description?: string;
  connectionId: number;
  sourceType: 'StoredProcedure' | 'Query' | 'TableOrView';
  isActive: boolean;
}

export interface SourceStats {
  totalRows: number;
  lastFetchedAt?: string;
  columns: ColumnInfo[];
}

export interface ColumnInfo {
  name: string;
  dataType: string;
  isNullable: boolean;
}

export interface FetchRequest {
  sourceId: number;
  parameters?: Record<string, unknown>;
}

export interface FetchResponse {
  jobId: string;
  status: 'Queued' | 'Running' | 'Completed' | 'Failed';
  message?: string;
}

export interface QueryResponse<T> {
  success: boolean;
  data: T[];
  rowCount: number;
  executionTimeMs: number;
  error?: string;
}

export interface BatchQueryResponse {
  success: boolean;
  results: Record<string, QueryResponse<Record<string, unknown>>>;
  totalExecutionTimeMs: number;
  error?: string;
}

// Data Types for specific reports
export interface KasaRaporuRow {
  FirmaKodu: string;
  KASANO: number;
  MAGAZA: string;
  KASATANIM: string;
  NAKIT: number;
  VISA: number;
  GPUSULASI: number;
  TOPLAM: number;
  FISSAYISI: number;
  POSDIZINI: string;
  IPTALTOP: number;
  INDIRIM: number;
  SEPETORT: number;
  SEC: string;
  SAAT: string;
  DURUM: string;
  ZKONTROL: string;
}

export interface CariHareketRow {
  CARIKODU: string;
  CARIADI: string;
  TARIH: string;
  BELGENOTU: string;
  BORC: number;
  ALACAK: number;
  BAKIYE: number;
}

// Kasa Hareketleri Raporu (NG_KASA_HAREKETLERI_RAPORU)
export interface KasaHareketleriRow {
  TARIH: string;
  KASAISLEM: string;
  ACIKLAMA: string;
  FIRMAADI: string | null;
  BANKAADI: string | null;
  BELGENO: string;
  SUBEADI: string;
  KASAADI: string;
  GELIR: number;
  GIDER: number;
  BAKIYE: number;
  PARABIRIMI: string;
  TLDEGER: number;
  [key: string]: unknown;
}

// Gruplandırılmış Kasa Özeti
export interface KasaOzetRow {
  KASAADI: string;
  SUBEADI: string;
  TOPLAMGELIR: number;
  TOPLAMGIDER: number;
  BAKIYE: number;
  ISLEMSAYISI: number;
  [key: string]: unknown;
}

// Şube Bazlı Kasa Özeti
export interface SubeKasaOzetRow {
  SUBEADI: string;
  TOPLAMGELIR: number;
  TOPLAMGIDER: number;
  BAKIYE: number;
  ISLEMSAYISI: number;
  [key: string]: unknown;
}

// Günlük Kasa Özeti
export interface KasaGunlukOzetRow {
  TARIH: string;
  GELIR: number;
  GIDER: number;
  BAKIYE: number;
  ISLEMSAYISI: number;
  [key: string]: unknown;
}

// Bankalar Raporu (NG_BANKALAR)
export interface BankalarRow {
  TARIH: string;
  BANKAKODU: string;
  BANKAADI: string;
  SUBE: string;
  BANKAKOD1: string;
  BELGENO: string;
  SUBEADI: string;
  KASAADI: string;
  BELGEIZAHAT: string;
  ISLEMIZAHAT: string;
  ACIKLAMA: string;
  FIRMAADI: string;
  VADE: string;
  BORC: number;
  ALACAK: number;
  BAKIYE: number;
  KUMULETIFBAKIYE: number;
  PARABIRIMI: string;
  KUR: number;
  TLBORC: number;
  TLALACAK: number;
  KUMULATIFTLBAKIYE: number;
  TLDEGER: number;
  KULLANICI: string;
  [key: string]: unknown;
}

// Gruplandırılmış Banka Özeti
export interface BankaOzetRow {
  BANKAADI: string;
  BANKAKOD1: string;
  TOPLAMBORC: number;
  TOPLAMALACAK: number;
  BAKIYE: number;
  ISLEMSAYISI: number;
  [key: string]: unknown;
}

// Günlük Banka Özeti
export interface BankaGunlukOzetRow {
  TARIH: string;
  BORC: number;
  ALACAK: number;
  BAKIYE: number;
  ISLEMSAYISI: number;
  [key: string]: unknown;
}

// Borçlular Raporu (NG_CARI_BORCLULAR)
export interface BorclularRow {
  FIRMA: string;
  FIRMAKODU: string;
  FIRMAADI: string;
  UNVAN: string | null;
  KOD1: string;
  KOD2: string;
  BORC: number;
  ALACAK: number;
  BAKIYE: number;
  DURUM: string;
  PARABIRIMI: string;
  TLDEGER: number;
  [key: string]: unknown;
}

// Alacaklılar Raporu (NG_CARI_ALACAKLILAR)
export interface AlacaklilarRow {
  FIRMA: string;
  FIRMAKODU: string;
  FIRMAADI: string;
  UNVAN: string;
  KOD1: string | null;
  KOD2: string | null;
  BORC: number;
  ALACAK: number;
  BAKIYE: number;
  DURUM: string;
  PARABIRIMI: string;
  TLDEGER: number;
  [key: string]: unknown;
}

// Verilen Çekler Raporu (NG_VERILENCEK)
export interface VerilenCeklerRow {
  FIRMAADI: string;
  CARIKOD1: string;
  CARIKOD2: string;
  VADE: string;
  ACIKLAMA: string;
  BASACIKLAMA: string | null;
  KESIDEEDENFIRMAKODU: string;
  KESIDEEDENFIRMAADI: string;
  CEKISLEM: string;
  CEKDURUM: string;
  KAYITTARIHI: string;
  SUBEADI: string;
  KASAADI: string;
  EVRAKNO: string;
  BANKAADI: string;
  BANKAKOD1: string;
  BANKAKOD2: string;
  BANKAKOD3: string;
  CCBELGENO: string;
  TUTAR: number;
  PARABIRIMI: string;
  TLDEGER: number;
  [key: string]: unknown;
}

// Alınan Çekler Raporu (NG_ALINAN_CEKLER)
export interface AlinanCeklerRow {
  FIRMAADI: string;
  CARIKOD1: string;
  CARIKOD2: string;
  VADE: string;
  ACIKLAMA: string;
  BASACIKLAMA: string | null;
  KESIDEEDENFIRMAKODU: string;
  KESIDEEDENFIRMAADI: string;
  CEKISLEM: string;
  CEKDURUM: string;
  KAYITTARIHI: string;
  SUBEADI: string;
  KASAADI: string;
  EVRAKNO: string;
  BANKAADI: string;
  BANKAKOD1: string;
  BANKAKOD2: string;
  BANKAKOD3: string;
  CCBELGENO: string;
  TUTAR: number;
  PARABIRIMI: string;
  TLDEGER: number;
  [key: string]: unknown;
}

// Fire Hareketleri Raporu (NG_DEPO_FIRE_HAREKETLERI)
export interface FireHareketleriRow {
  YIL: number;
  AY: number;
  CIKANTUTAR: number;
  GIRENTUTAR: number;
  TEKIL: string;
  IND: number;
  BELGENO: string;
  TARIH: string;
  STOKKODU: string;
  MALINCINSI: string;
  STOKTIPI: number;
  GIREN: number;
  CIKAN: number;
  GIRDIGIDEPO: string;
  CIKTIGIDEPO: string;
  CIKISDEPONO: number;
  GIRISDEPONO: number;
  ACIKLAMA: string | null;
  OZELKOD1: string | null;
  OZELKOD2: string | null;
  OZELKOD3: string | null;
  OZELKOD4: string | null;
  OZELKOD5: string | null;
  OZELKOD6: string | null;
  OZELKOD7: string | null;
  OZELKOD8: string | null;
  STOKNO: number;
  AFIYATI: number;
  BELGETIPI: number;
  [key: string]: unknown;
}

// Gruplandırılmış Fire Özeti
export interface FireOzetRow {
  CIKTIGIDEPO: string;
  TOPLAMTUTAR: number;
  TOPLAMMIKTAR: number;
  ISLEMSAYISI: number;
  [key: string]: unknown;
}

// Giderler Raporu (NG_ALIS_ANALIZ_GIDERLER)
export interface GiderlerRow {
  DEPOKODU: string;
  OZELKOD1: string;
  TARIH: string;
  STOKHAREKETIZAHAT: string;
  IZAHAT: string;
  STOKKODU: string;
  MALINCINSI: string;
  NETTUTAR: number;
  NETTUTARDAHIL: number;
  OZELKOD4: string;
  FIRMAKODU: string;
  FIRMAADI: string;
  [key: string]: unknown;
}

// Gruplandırılmış Gider Özeti
export interface GiderlerOzetRow {
  DEPOKODU: string;
  TOPLAMTUTAR: number;
  ISLEMSAYISI: number;
  [key: string]: unknown;
}

// Stok Maliyet Raporu (NG_STOK_DEGER_RAPORU) - Stok kartı alış fiyatı üzerinden
export interface StokMaliyetRow {
  FIRMA: string;
  DEPOADI: string;
  STOKKODU: string;
  MALINCINSI: string;
  KOD1: string;
  KOD2: string;
  KOD3: string;
  KOD4: string;
  KOD5: string;
  KOD6: string | null;
  KOD7: string | null;
  KOD8: string | null;
  KOD9: string;
  KOD10: string | null;
  BIRIMMALIYET: number;
  KALAN: number;
  ALISFIYATI: number;
  ALISKDVORANI: number;
  STATUS: number;
  STOKTIPI: string;
  TUTAR: number;
  [key: string]: unknown;
}

// Stok FIFO Raporu (F0107D0005_SP_StokFifoRaporu)
export interface StokFifoRow {
  FIRMA: string;
  DEPOADI: string;
  STOKKODU: string;
  MALINCINSI: string;
  KOD1: string;
  KOD2: string;
  KOD3: string;
  KOD4: string;
  KOD5: string;
  KOD6: string | null;
  KOD7: string | null;
  KOD8: string | null;
  KOD9: string;
  KOD10: string | null;
  BIRIMMALIYET: number;
  KALAN: number;
  ALISFIYATI: number;
  MALIYET: number;
  ALISKDVORANI: number;
  STATUS: number;
  STOKTIPI: string;
  TUTAR: number;
  [key: string]: unknown;
}

// Dashboard Types
export interface DashboardStat {
  title: string;
  value: number | string;
  prefix?: React.ReactNode;
  suffix?: string;
  color?: string;
}

// Top Satış Stokları (Miktar bazlı) - NG_TOP_SATIS_STOKLARI (Procedure 29)
export interface TopSatisStokMiktarRow {
  STOKKODU: string;
  MALINCINSI: string;
  TOPLAMCIKAN: number;
  [key: string]: unknown;
}

// Top Satış Stokları (Tutar bazlı) - NG_TOP_SATIS_STOKLARI_SUBE (Procedure 30)
export interface TopSatisStokTutarRow {
  STOKKODU: string;
  MALINCINSI: string;
  TOPLAMTUTAR: number;
  [key: string]: unknown;
}

// Şube Net Satış Raporu - NG_SUBE_NETSATIS_RAPORU (Procedure 31)
export interface SubeNetSatisRow {
  MAGAZA: string;
  TOPLAMNETATATIS: number;
  [key: string]: unknown;
}

// Auth Types
export type UserRole = 'admin' | 'user' | 'viewer';

export interface User {
  id: number;
  email: string;
  name: string;
  role: UserRole;
  firmId?: number;
  firmName?: string;
  schemaName?: string;
  firmRoleId?: number;
  firmRoleName?: string;  // Role name from backend (e.g., 'Admin', 'Muhasebe', 'Satış')
}

// Admin kullanıcı kontrolü
export function isAdminUser(user: User | null): boolean {
  return user?.role === 'admin';
}

// This is now defined below with extended fields

export interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  firmStatus: FirmStatus | null;
  announcements: Announcement[];
}

// Firm Status Types
export interface FirmStatus {
  status: 'active' | 'expired' | 'deactivated';
  deactivationReason?: string;
  licenseExpiresAt?: string;
}

// Announcement Types
export interface Announcement {
  id: number;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'success' | 'error';
  startsAt: string;
  endsAt: string;
}

// Extended Login Response
// NOTE: allowedReports removed - permissions now determined by rolePermissions.ts config
export interface LoginResponse {
  token: string;
  user: User;
  firmStatus?: string;
  deactivationReason?: string;
  licenseExpiresAt?: string;
  announcements?: Announcement[];
}
